import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Calendar } from 'lucide-react';
import { eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const GENRES = ['music','film','art','tech','fashion','gaming','education','community','other'];

export default function CreateEventPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', genre: 'music', location: '', venue: '', event_date: '', event_time: '19:00' });
  const [tiers, setTiers] = useState([
    { name: 'General Admission', price: 25, quantity: 100, perks: 'Standard entry' },
    { name: 'VIP', price: 75, quantity: 30, perks: 'Priority entry + backstage' },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const addTier = () => { if (tiers.length < 5) setTiers([...tiers, { name: '', price: 25, quantity: 50, perks: '' }]); };
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const updateTier = (i, field, val) => { const t = [...tiers]; t[i][field] = val; setTiers(t); };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.location.trim()) { toast.error('Location required'); return; }
    if (!form.event_date) { toast.error('Date required'); return; }
    setSubmitting(true);
    try {
      const res = await eventsAPI.create({ ...form, ticket_tiers: tiers });
      toast.success('Event created!');
      navigate(`/events/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-12" data-testid="create-event-page">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-2xl mx-auto px-4 py-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)}><ArrowLeft size={20} className="text-zinc-600" /></button>
          <div>
            <h1 className="font-bold text-xl text-zinc-900">Create Event</h1>
            <p className="text-xs text-zinc-500">Set up tickets and start selling</p>
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-3">
          <Input placeholder="Event Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="text-lg font-bold" data-testid="event-title-input" />
          <Textarea placeholder="Event description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} data-testid="event-desc-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Location (city)" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="event-location" />
            <Input placeholder="Venue name" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Select value={form.genre} onValueChange={v => setForm({ ...form, genre: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{GENRES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} data-testid="event-date" />
            <Input type="time" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-zinc-900">Ticket Tiers</h2>
            <Button variant="outline" size="sm" onClick={addTier} disabled={tiers.length >= 5}><Plus size={14} className="mr-1" /> Add Tier</Button>
          </div>
          <div className="space-y-3">
            {tiers.map((t, i) => (
              <div key={i} className="p-4 bg-zinc-50 rounded-xl border border-zinc-200 relative">
                {tiers.length > 1 && <button onClick={() => removeTier(i)} className="absolute top-2 right-2 text-zinc-400 hover:text-red-500"><X size={14} /></button>}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <Input placeholder="Tier name" value={t.name} onChange={e => updateTier(i, 'name', e.target.value)} className="text-sm" />
                  <Input type="number" min={0} placeholder="Price ($)" value={t.price} onChange={e => updateTier(i, 'price', parseFloat(e.target.value) || 0)} className="text-sm" />
                  <Input type="number" min={1} placeholder="Quantity" value={t.quantity} onChange={e => updateTier(i, 'quantity', parseInt(e.target.value) || 1)} className="text-sm" />
                </div>
                <Input placeholder="Perks description" value={t.perks} onChange={e => updateTier(i, 'perks', e.target.value)} className="text-sm" />
              </div>
            ))}
          </div>
        </div>

        <Button onClick={handleSubmit} disabled={submitting} className="w-full h-12 bg-violet-600 text-white font-bold text-lg rounded-xl" data-testid="create-event-submit">
          <Calendar size={18} className="mr-2" /> {submitting ? 'Creating...' : 'Create Event'}
        </Button>
      </div>
    </div>
  );
}
